// ─────────────────────────────────────────────────────────────────
// Glass-i — AppHeader page-title table + resolver (Stage 6B.9H).
//
// Pure JS — no React imports. Extracted from AppHeader.jsx so the
// mapping can be exercised by a node:test runner. The longest-prefix
// rule still applies; entries earlier in the table win on equal-depth
// matches, so list the more specific paths first.
// ─────────────────────────────────────────────────────────────────

export const PAGE_TITLES = [
  ['/subscription/renew', 'Renew subscription'],
  ['/subscription', 'Subscription'],
  ['/invoices', 'Invoices'],
  ['/users', 'Users'],
  ['/profile', 'My profile'],
  ['/session', 'Session activity'],
  ['/reports/viewer', 'Report viewer'],
  // Stage 6A.2 — production Customer Master.
  ['/erp/masters/customers', 'Customer Master'],
  // Stage 6B.3 — Supplier Master.
  ['/erp/masters/suppliers', 'Supplier Master'],
  // Stage 6B.8A-1 — UOM Master (with GSTN UQC mapping).
  ['/erp/masters/uoms', 'UOM Master'],
  // Stage 6B.8A-2A — Item Configuration Masters.
  ['/erp/masters/item-config', 'Item Configuration'],
  // Stage 6B.8A-3I — Ready-Made Glass Concepts.
  ['/erp/masters/glass-concepts', 'Concept Studio'],
  // Stage 6B.8A-3K — Process / Fabrication Master.
  ['/erp/masters/processes', 'Process Master'],
  // Stage 6B.8A-3K.1 — Thickness Master.
  ['/erp/masters/thicknesses', 'Thickness Master'],
  // Stage 6OC.1 — Other Charges Master.
  ['/erp/masters/other-charges', 'Other Charges Master'],
  // Stage 6Q — Manual Charge Master.
  ['/erp/masters/manual-charges', 'Manual Charge Master'],
  // Stage 6Q.1 — Sales Proforma (longest-prefix first).
  ['/erp/sales/proforma-approvals', 'Proforma Approvals'],
  ['/erp/sales/proformas/new', 'New Proforma'],
  ['/erp/sales/proformas/:id', 'Proforma'],
  ['/erp/sales/proformas', 'Proformas'],
  // Stage 6B.8A-3K.3 — Production Department Master.
  ['/erp/masters/production-departments', 'Production Departments'],
  // Stage 6B.8A-3N — Glass Category Master (drives Item-create dropdown).
  ['/erp/masters/glass-categories', 'Glass Category Master'],
  // HRMS Hub + Stage HRMS-1 foundation (masters + salesperson mapping).
  ['/erp/hrms',                     'HRMS Overview'],
  ['/erp/hrms/employees',           'Employees'],
  ['/erp/hrms/departments',         'Departments'],
  ['/erp/hrms/designations',        'Designations'],
  ['/erp/hrms/branches',            'Branches'],
  ['/erp/hrms/salesperson-mapping', 'Customer Salesperson Mapping'],
  // Stage HRMS-3 — organization / transfers / history.
  ['/erp/hrms/organization-chart',  'Organization Chart'],
  ['/erp/hrms/employee-transfers',  'Employee Transfers'],
  ['/erp/hrms/employee-history',    'Employee History'],
  // Stage 6B.8A-3M — Glass Type + Fabrication Masters (inline CRUD).
  ['/erp/masters/glass-types',  'Glass Type Master'],
  ['/erp/masters/glass-type-routing', 'Glass Type Routing'],
  ['/erp/masters/fabrications', 'Fabrication Master'],
  // Stage 6CM.2 — Proforma Settings page removed (superseded by Company Defaults).
  // Stage 6Q.4 — tenant-wide Proforma grid column visibility.
  ['/erp/masters/proforma-grid-settings', 'Proforma Grid Columns'],
  // Stage 6CM.1 — Company Defaults Master (central default setup values).
  ['/erp/masters/company-defaults', 'Company Defaults'],
  // Stage 6P.1 + 6P.2 — Purchase Request + Purchase Order surfaces.
  // List the `/new` literals BEFORE the parameterised `/:id` so the
  // longest-prefix walk lands the correct title on the create surface.
  ['/erp/purchase/requests/new',     'New Purchase Request'],
  ['/erp/purchase/requests/:id',     'Purchase Request'],
  ['/erp/purchase/requests',         'Purchase Requests'],
  ['/erp/purchase/orders/new',       'New Purchase Order'],
  ['/erp/purchase/orders/:id',       'Purchase Order'],
  ['/erp/purchase/orders',           'Purchase Orders'],
  // Stage 6P.3 — GRN / Material Receipt Foundation. Literals BEFORE
  // the parameterised `/:id` per the longest-prefix walk.
  ['/erp/purchase/grns/new',         'New Goods Receipt Note'],
  ['/erp/purchase/grns/from-po',     'Create GRN from PO'],
  ['/erp/purchase/grns/:id/report',  'GRN Receipt'],
  ['/erp/purchase/grns/:id',         'Goods Receipt Note'],
  ['/erp/purchase/grns',             'Goods Receipt Notes'],
  // Stage 6P.4 — Purchase Bill Foundation + PO/GRN Matching. Literals
  // BEFORE the parameterised `/:id` per the longest-prefix walk.
  ['/erp/purchase/bills/new',         'New Purchase Bill'],
  ['/erp/purchase/bills/from-po',     'Create Purchase Bill from PO'],
  ['/erp/purchase/bills/from-grn',    'Create Purchase Bill from GRN'],
  ['/erp/purchase/bills/:id',         'Purchase Bill'],
  ['/erp/purchase/bills',             'Purchase Bills'],
  // Stage 6P.5 — Inventory Inward Posting Foundation. Literal `/grn`
  // and `/bill` deep-link prefixes BEFORE the bare `/purchase-inward`
  // list page so the longest-prefix walk lands the correct title.
  ['/erp/inventory/purchase-inward/grn',     'Post GRN to Inventory'],
  ['/erp/inventory/purchase-inward/bill',    'Post Purchase Bill to Inventory'],
  ['/erp/inventory/purchase-inward',         'Purchase Inward'],
  ['/erp/inventory/stock-ledger',            'Stock Ledger'],
  ['/erp/inventory/stock',                   'Current Stock'],
  // Stage 6P.6 — Purchase Bill Accounting Posting Foundation. Literal
  // segments (`post`, `postings`, `gst-itc`, `unposted-bills`,
  // `supplier-payables`) come BEFORE any parameterised `:id` so the
  // longest-prefix walk lands the correct title on each surface.
  ['/erp/purchase-accounting/post',               'Post Purchase Bill to Accounts'],
  ['/erp/purchase-accounting/postings/:id',       'Purchase Accounting Posting'],
  ['/erp/purchase-accounting/postings',           'Purchase Accounting'],
  ['/erp/purchase-accounting/gst-itc',            'Purchase GST ITC Register'],
  ['/erp/purchase-accounting/unposted-bills',     'Unposted Purchase Bills'],
  ['/erp/purchase-accounting/supplier-payables',  'Supplier Payables'],
  // Stage 6P.7 — Supplier Payment + Payment Voucher Integration. Literal
  // segments (`new`, `payables`, `register`, `history`) come BEFORE the
  // parameterised `:id` so the longest-prefix walk lands the correct
  // title on each surface. Bare list comes last so it doesn't shadow
  // any of the literals above it.
  ['/erp/supplier-payments/new',                  'New Supplier Payment'],
  ['/erp/supplier-payments/payables',             'Supplier Payables'],
  ['/erp/supplier-payments/register',             'Supplier Payment Register'],
  ['/erp/supplier-payments/history',              'Supplier Payment History'],
  ['/erp/supplier-payments/:id',                  'Supplier Payment'],
  ['/erp/supplier-payments',                      'Supplier Payments'],
  // Stage 6P.8 — Purchase Return / Debit Note Foundation. Literal
  // segments (`new`, `register`, `itc-reversals`, `from-inventory-ledger`,
  // `from-purchase-bill`) come BEFORE the parameterised `:id` so the
  // longest-prefix walk lands the correct title on each surface. Bare
  // list comes last so it doesn't shadow any of the literals above it.
  ['/erp/purchase-returns/new',                                  'New Purchase Return'],
  ['/erp/purchase-returns/register',                             'Purchase Return Register'],
  ['/erp/purchase-returns/itc-reversals',                        'Purchase ITC Reversals'],
  ['/erp/purchase-returns/from-inventory-ledger',                'Create Purchase Return from Inventory'],
  ['/erp/purchase-returns/from-purchase-bill',                   'Create Purchase Return from Purchase Bill'],
  ['/erp/purchase-returns/:id',                                  'Purchase Return'],
  ['/erp/purchase-returns',                                      'Purchase Returns'],
  // Stage 6P.9 — TDS / TCS Foundation. The three `/reports/*` paths
  // come BEFORE the top-level surfaces so the longest-prefix walk
  // lands the right title on each report tab. RBAC server-side via
  // `erp.tds_tcs.*` permissions.
  ['/erp/tds-tcs/reports/supplier-summary',  'Supplier-wise TDS Summary'],
  ['/erp/tds-tcs/reports/section-summary',   'Section-wise TDS Summary'],
  ['/erp/tds-tcs/reports/pending-challans',  'Pending TDS Challans'],
  ['/erp/tds-tcs/rules',                     'TDS/TCS Rules'],
  ['/erp/tds-tcs/supplier-profiles',         'Supplier Tax Profiles'],
  ['/erp/tds-tcs/deductions',                'TDS/TCS Deductions'],
  ['/erp/tds-tcs/challans',                  'TDS Challans'],
  // Stage 6B.2 — Accounts setup surfaces (must precede the generic
  // /erp/accounts prefix so deeper paths get the specific title).
  ['/erp/accounts/setup',              'Accounts Setup'],
  ['/erp/accounts/document-sequences', 'Document Sequences'],
  ['/erp/accounts/opening-balances',   'Opening Balances'],
  ['/erp/accounts/ledgers',            'Ledger Accounts'],
  ['/erp/accounts/taxes',              'Tax Rates'],
  ['/erp/accounts/periods',            'Accounting Periods'],
  ['/erp/accounts/vouchers',           'Journal Vouchers'],
  // Stage 6B.6 — payment / receipt vouchers.
  ['/erp/accounts/payments',           'Payment / Receipt'],
  // Stage 6B.7 — expense entry (paid + credit).
  ['/erp/accounts/expenses',           'Expense Entry'],
  // Stage 6B.9C — Contra Voucher (cash↔bank, bank↔bank, cash↔cash).
  ['/erp/accounts/contras',            'Contra Voucher'],
  // Stage 6B.9D — Fixed Assets Register.
  ['/erp/accounts/fixed-assets',       'Fixed Assets'],
  // Stage 6B.9E — Investments Register.
  ['/erp/accounts/investments',        'Investments'],
  // Stage 6E.1 — Bank Reconciliation + Cheque Tracking.
  ['/erp/accounts/bank-reconciliations', 'Bank Reconciliation'],
  ['/erp/accounts/cheques',              'Cheque Tracking'],
  // Stage 6E.2 — Year-End Closing.
  ['/erp/accounts/year-end-closing',     'Year-End Closing'],
  // Stage 6B.5 — read-only account reports.
  ['/erp/accounts/reports/trial-balance',    'Trial Balance'],
  ['/erp/accounts/reports/ledger-statement', 'Ledger Statement'],
  ['/erp/accounts/reports/voucher-register', 'Voucher Register'],
  ['/erp/accounts/reports/account-balances', 'Account Balances'],
  // Stage 6B.7E — Profit & Loss + Balance Sheet.
  ['/erp/accounts/reports/profit-loss',      'Profit & Loss'],
  ['/erp/accounts/reports/balance-sheet',    'Balance Sheet'],
  // Stage 6B.9G — Schedule reports.
  ['/erp/accounts/reports/fixed-asset-schedule', 'Fixed Asset Schedule'],
  ['/erp/accounts/reports/investment-schedule',  'Investment Schedule'],
  // Stage 6E.3 — Debtors / Creditors Aging.
  ['/erp/accounts/reports/debtors-aging',        'Debtors Aging'],
  ['/erp/accounts/reports/creditors-aging',      'Creditors Aging'],
  ['/erp/accounts/reports',            'Account Reports'],
  ['/erp/accounts/gst-reports',        'GST Reports'],
  ['/erp/accounts',                    'Accounts'],
  // Stage 6A foundation — reusable ERP grid preview route.
  ['/dev/erp-grid-demo', 'Customer Master Grid Demo']
];

// Map the current pathname to a human-readable page title used in
// the topbar. Longest-prefix wins so /invoices/:id and
// /reports/viewer/:jobId still resolve. Returns null for routes we
// don't have a title for (don't render an empty header in that case).
export function pageTitleFromPath(pathname) {
  if (!pathname) return null;
  for (const [prefix, title] of PAGE_TITLES) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return title;
  }
  return null;
}
